import { typedSupabase } from "../../lib/typed-supabase";
import type { IService } from "../service-registry";
import type {
  CreateNotificationData,
  Notification,
} from "../../types/notification";
import { BaseNotificationService, type NotificationServiceResponse } from "./base-notification.service";

/**
 * Notification CRUD Interface
 * Handles persistence operations: Create, Read (single), Update, Delete
 */
export interface INotificationCRUD extends IService {
  /**
   * Create a new notification in the database
   */
  createNotification(
    data: CreateNotificationData
  ): Promise<NotificationServiceResponse<Notification>>;

  /**
   * Delete a notification by ID
   */
  deleteNotification(notificationId: string): Promise<NotificationServiceResponse<boolean>>;

  /**
   * Delete all read notifications for a user
   */
  deleteReadNotifications(userId: string): Promise<NotificationServiceResponse<number>>;

  /**
   * Delete read notifications older than a specified date
   */
  deleteOldReadNotifications(
    userId: string,
    beforeDate?: Date
  ): Promise<NotificationServiceResponse<number>>;

  /**
   * Update notification metadata (internal fields only)
   */
  updateNotificationData(
    notificationId: string,
    updates: Partial<Notification>
  ): Promise<NotificationServiceResponse<Notification>>;
}

/**
 * Notification CRUD Service
 * Handles all Create, Read (single), Update, Delete operations
 * Single responsibility: Persistence layer
 *
 * @example
 * ```typescript
 * const result = await notificationCRUD.createNotification({
 *   user_id: "user-123",
 *   type: "task_assigned",
 *   message_preview: "New task assigned",
 * });
 *
 * if (result.error) {
 *   console.error("Failed to create:", result.error);
 * } else {
 *   console.log("Created:", result.data.id);
 * }
 * ```
 */
class NotificationCRUD extends BaseNotificationService implements INotificationCRUD {
  readonly name = "notification-crud" as const;
  protected readonly serviceName = this.name;

  async initialize(): Promise<void> {
    // Service is stateless, no initialization needed
  }

  async dispose(): Promise<void> {
    // Service is stateless, no cleanup needed
  }

  /**
   * Create a new notification in the database
   *
   * @param data - Notification data with user_id, type, and message_preview
   * @returns Created notification with ID and timestamps
   *
   * @example
   * ```typescript
   * const result = await notificationCRUD.createNotification({
   *   user_id: "user-123",
   *   type: "task_assigned",
   *   message_preview: "Your task is ready",
   *   data: { taskId: "task-456" },
   *   entity_type: "task",
   *   entity_id: "task-456",
   * });
   * ```
   */
  async createNotification(
    data: CreateNotificationData
  ): Promise<NotificationServiceResponse<Notification>> {
    // Validate input
    const validationError = this.validateCreateData(data);
    if (validationError) {
      return {
        data: null,
        error: {
          code: "VALIDATION_ERROR",
          message: validationError,
          service: this.serviceName,
        },
      };
    }

    return this.executeOperation<Notification>(
      "NOTIFICATION_CREATE",
      async () => {
        const { data: newNotif, error } = await typedSupabase
          .from("notifications")
          .insert({
            user_id: data.user_id,
            type: data.type,
            message_preview: data.message_preview,
            data: data.data || null,
            actor_id: data.actor_id || null,
            entity_type: data.entity_type || null,
            entity_id: data.entity_id || null,
            needs_multi_channel: true,
          })
          .select()
          .single();
        return { data: newNotif as unknown as Notification, error };
      },
      { userId: data.user_id, type: data.type }
    );
  }

  /**
   * Delete a notification by ID
   *
   * @param notificationId - ID of notification to delete
   * @returns true if deleted successfully
   *
   * @example
   * ```typescript
   * const result = await notificationCRUD.deleteNotification("notif-123");
   * if (result.data) {
   *   console.log("Deleted successfully");
   * }
   * ```
   */
  async deleteNotification(
    notificationId: string
  ): Promise<NotificationServiceResponse<boolean>> {
    if (!notificationId) {
      return {
        data: null,
        error: {
          code: "VALIDATION_ERROR",
          message: "notificationId is required",
          service: this.serviceName,
        },
      };
    }

    return this.executeOperation(
      "NOTIFICATION_DELETE",
      async () => {
        const result = await typedSupabase
          .from("notifications")
          .delete()
          .eq("id", notificationId);

        return { data: (result.count ?? 0) > 0, error: result.error };
      },
      { notificationId }
    );
  }

  /**
   * Delete all read notifications for a user
   * Immediately removes read notifications from the database
   *
   * @param userId - User ID
   * @returns Number of deleted notifications
   *
   * @example
   * ```typescript
   * const result = await notificationCRUD.deleteReadNotifications("user-123");
   * if (result.data !== null) {
   *   console.log(`Deleted ${result.data} read notifications`);
   * }
   * ```
   */
  async deleteReadNotifications(
    userId: string
  ): Promise<NotificationServiceResponse<number>> {
    if (!userId) {
      return {
        data: null,
        error: {
          code: "VALIDATION_ERROR",
          message: "userId is required",
          service: this.serviceName,
        },
      };
    }

    return this.executeOperation<number>(
      "NOTIFICATION_DELETE_READ",
      async () => {
        const { data, error } = await typedSupabase.rpc(
          "cleanup_all_read_notifications",
          { p_user_id: userId }
        );

        return { data: data || 0, error };
      },
      { userId }
    );
  }

  /**
   * Delete read notifications older than a specified date
   * Used to clean up old read notifications that clutter the UI
   *
   * @param userId - User ID
   * @param beforeDate - Delete notifications created before this date (default: 30 days ago)
   * @returns Number of deleted notifications
   *
   * @example
   * ```typescript
   * // Delete read notifications older than 30 days
   * const result = await notificationCRUD.deleteOldReadNotifications("user-123");
   * if (result.data) {
   *   console.log(`Deleted ${result.data} old read notifications`);
   * }
   * ```
   */
  async deleteOldReadNotifications(
    userId: string,
    beforeDate?: Date
  ): Promise<NotificationServiceResponse<number>> {
    if (!userId) {
      return {
        data: null,
        error: {
          code: "VALIDATION_ERROR",
          message: "userId is required",
          service: this.serviceName,
        },
      };
    }

    const daysOld = beforeDate
      ? Math.floor((Date.now() - beforeDate.getTime()) / (1000 * 60 * 60 * 24))
      : 90;

    return this.executeOperation<number>(
      "NOTIFICATION_DELETE_OLD_READ",
      async () => {
        const { data, error } = await typedSupabase.rpc(
          "cleanup_user_old_notifications",
          { p_user_id: userId, p_days_old: daysOld }
        );

        return { data: data || 0, error };
      },
      { userId, daysOld }
    );
  }

  /**
   * Update notification metadata
   * Only use this for updating internal fields like data, actor_id, etc.
   * For state changes (is_read), use NotificationState service instead.
   *
   * @param notificationId - ID of notification to update
   * @param updates - Partial notification object with fields to update
   * @returns Updated notification
   *
   * @example
   * ```typescript
   * const result = await notificationCRUD.updateNotificationData(
   *   "notif-123",
   *   { data: { newField: "value" } }
   * );
   * ```
   */
  async updateNotificationData(
    notificationId: string,
    updates: Partial<Notification>
  ): Promise<NotificationServiceResponse<Notification>> {
    if (!notificationId) {
      return {
        data: null,
        error: {
          code: "VALIDATION_ERROR",
          message: "notificationId is required",
          service: this.serviceName,
        },
      };
    }

    if (!updates || Object.keys(updates).length === 0) {
      return {
        data: null,
        error: {
          code: "VALIDATION_ERROR",
          message: "At least one field must be provided for update",
          service: this.serviceName,
        },
      };
    }

    return this.executeOperation<Notification>(
      "NOTIFICATION_UPDATE",
      async () => {
        const { data: updatedNotif, error } = await typedSupabase
          .from("notifications")
          .update(updates)
          .eq("id", notificationId)
          .select()
          .single();
        return { data: updatedNotif as unknown as Notification, error };
      },
      { notificationId, fieldsUpdated: Object.keys(updates).length }
    );
  }

  /**
   * Validate create notification data
   */
  private validateCreateData(data: CreateNotificationData): string | null {
    if (!data.user_id) return "user_id is required";
    if (!data.type) return "type is required";
    if (!data.message_preview) return "message_preview is required";
    if (data.message_preview.length > 500)
      return "message_preview cannot exceed 500 characters";

    return null;
  }
}

export const notificationCRUD = new NotificationCRUD();
export { NotificationCRUD };
